/**
 * Lógica pura de la cola del diario de comidas: fusión de las operaciones
 * `meal.sync` pendientes en un único lote.
 *
 * Igual que `coalesce.ts` para el entrenamiento, aquí no se toca ni el DOM ni
 * Dexie ni la red: son funciones sobre datos, para poder ejecutarlas en Node y
 * comprobarlas sin navegador (la app web no tiene `vitest` configurado).
 */
import type { MealEntryUpsert, MealSyncRequest } from '@fitness/api-client';
import type { SyncOp } from '@fitness/core';

/** Único `kind` de la cola de comidas: un lote de `POST /v1/meals/sync`. */
export const MEAL_SYNC_KIND = 'meal.sync';

/** Tope por array que impone el contrato en `MealSyncRequest`. */
export const MAX_MEAL_BATCH = 200;

/** Resultado de fusionar varias operaciones de comida en una sola. */
export interface MealCoalesceResult {
  /** El lote combinado, con los arrays vacíos omitidos. */
  request: MealSyncRequest;
  /** Identificadores de las operaciones que se han absorbido. */
  consumedIds: string[];
  /** `createdAt` de la más antigua: la combinada hereda su sitio en la cola. */
  createdAt: number;
}

function isMealRequest(value: unknown): value is MealSyncRequest {
  return typeof value === 'object' && value !== null;
}

function compareOps(a: SyncOp, b: SyncOp): number {
  if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * ¿Cabe el lote sin pasarse del tope? Cuenta sólo las claves nuevas, porque las
 * repetidas sustituyen a las que ya estaban.
 */
function fits(
  payload: MealSyncRequest,
  entries: Map<string, MealEntryUpsert>,
  deleted: Set<string>,
  max: number,
): boolean {
  let entryCount = entries.size;
  let deletedCount = deleted.size;
  for (const item of payload.entries ?? []) {
    if (!entries.has(item.clientId)) entryCount += 1;
  }
  for (const id of payload.deletedClientIds ?? []) {
    if (!deleted.has(id)) deletedCount += 1;
  }
  return entryCount <= max && deletedCount <= max;
}

/**
 * Fusiona las operaciones pendientes en un único `MealSyncRequest`.
 *
 * Se recorren en orden de creación y gana la última versión de cada `clientId`:
 * corregir tres veces los gramos de la misma entrada acaba en un solo elemento.
 * El servidor aplica dentro del lote primero las altas y después los borrados,
 * así que un borrado posterior saca la entrada del array `entries` y un alta
 * posterior la saca de `deletedClientIds`.
 *
 * Se para en cuanto la siguiente operación no cabría en {@link MAX_MEAL_BATCH};
 * lo que quede se fusiona en la pasada siguiente. La primera se acepta siempre
 * (ya era un lote legal por sí misma), de modo que la cola nunca se atasca.
 *
 * Devuelve `null` cuando no hay nada que fusionar (menos de dos operaciones).
 */
export function coalesceMealOps(
  ops: readonly SyncOp[],
  maxPerArray: number = MAX_MEAL_BATCH,
): MealCoalesceResult | null {
  const candidates = ops.filter((op) => op.kind === MEAL_SYNC_KIND).sort(compareOps);
  if (candidates.length < 2) return null;

  const entries = new Map<string, MealEntryUpsert>();
  const deleted = new Set<string>();
  const consumedIds: string[] = [];

  for (const op of candidates) {
    const payload = op.payload;
    if (!isMealRequest(payload)) {
      // Operación corrupta: se absorbe y se descarta, no tiene sentido reenviarla.
      consumedIds.push(op.id);
      continue;
    }
    if (consumedIds.length > 0 && !fits(payload, entries, deleted, maxPerArray)) break;

    for (const entry of payload.entries ?? []) {
      entries.set(entry.clientId, entry);
      deleted.delete(entry.clientId);
    }
    for (const id of payload.deletedClientIds ?? []) {
      entries.delete(id);
      deleted.add(id);
    }
    consumedIds.push(op.id);
  }

  if (consumedIds.length < 2) return null;

  const request: MealSyncRequest = {};
  if (entries.size > 0) request.entries = Array.from(entries.values());
  if (deleted.size > 0) request.deletedClientIds = Array.from(deleted);

  const first = candidates[0];
  return { request, consumedIds, createdAt: first ? first.createdAt : 0 };
}
